import {
  IRecord_Booking_Core_Editable,
  IRecord_Booking_Core,
  IData_Booking_Settings_Org,
  IRecord_Booking_Core_Viewable,
  IData_Booking_Availability,
  IData_Calendar_Public_Day,
  IData_Calendar_Public_Day_Merged,
  IData_Booking_Availability_Time,
  IData_Booking_Availability_Time_Slot,
  IData_Calendar_Public_Slot_Merged,
} from '../types/schema/booking'

import {
  IRecord_CRMContact_Import,
} from '../types/schema/crm'

import {
  getDateDayOfWeek,
  doesDateMatchDateSlots,
  getTimeFromDateString,
  getDateString,
  getTimeTitle,
  adjustTime,
  isTimeBeforeOrSame,
  doTimeSlotsIntersect,
  getDateEpochDays,
  getDaysFromToday,
  getOutcode,
  doesETCoverOutcode,
} from './utils'

// given a "IRecord_Booking_Core_Editable" = the thing we edit in the browser
// get Partials of the things we can save in the database
export const parseEditableBookingRecord = (editable: IRecord_Booking_Core_Editable): {
  core: IRecord_Booking_Core,
  crm: IRecord_CRMContact_Import,
  tags: string[],
} => {
  const payments = editable.payments || []
  const cost = (editable.product.price || 0) * 100
  const paid = payments.reduce<number>((all, payment) => {
    return all + payment.amount
  }, 0)
  return {
    crm: editable.crm,
    tags: editable.tags,
    core: {
      cost,
      paid,
      crm_contact: '',
      assigned_et: editable.assigned_et,
      date: editable.date,
      reference: editable.reference,
      state: editable.state,
      data: {
        product: editable.product,
        prebooking: editable.prebooking,
        presurvey: editable.presurvey,
        postsurvey: editable.postsurvey,
        monthfeedback: editable.monthfeedback,
        yearfeedback: editable.yearfeedback,
        payments: editable.payments || [],
        notes: editable.notes,
      }
    }
  }
}

// given the availability for one person
// work out what time slots are free for a given day
//  * work through overrides to see if there is a hit - if yes then no time slots
//  * otherwise match the day of week to the basline and return it's time slots
export const getAvailableTimeSlots = (
  availability: IData_Booking_Availability[],
  date: string,
): IData_Booking_Availability_Time_Slot[] => {

  const daysIntoFuture = getDaysFromToday(date)
  const dayOfWeek = getDateDayOfWeek(date)

  // if the asked for date is a holiday then we have no slots
  const holidays = availability.filter(a => a.holiday ? true : false)
  const matchingHoliday = holidays.find(a => doesDateMatchDateSlots(date, a.dateSlots))
  if (matchingHoliday) return []

  const dateBasedAvailability = availability.filter(a => {
    if(a.holiday) return false
    return a.dateSlots && a.dateSlots.length > 0
  })

  // first lets test the date based settings
  const dateBasedSetting = dateBasedAvailability.find(a => {
    const dateMatch = doesDateMatchDateSlots(date, a.dateSlots)
    const dayOfWeekMatch = a.daysOfWeek.length > 0 ?
      a.daysOfWeek.includes(dayOfWeek) :
      true
    return dateMatch && dayOfWeekMatch
  })

  if(dateBasedSetting) return dateBasedSetting.timeSlots

  const daysOfWeekAvailability = availability.filter(a => {
    if(a.holiday) return false
    if(!a.daysOfWeek || a.daysOfWeek.length <= 0) return false
    if(a.dateSlots && a.dateSlots.length > 0) return false
    // only use daysOfWeek settings that are within the given window into the future
    // this is to stop auto-dates lasting forever and someone booking 3 years into the future
    if(a.activeDays > 0 && a.activeDays < daysIntoFuture) return false
    return true
  })

  // ok let's try matching the day of week
  const dayOfWeekBasedSetting = daysOfWeekAvailability.find(a => {
    return a.daysOfWeek.includes(dayOfWeek)
  })

  if(dayOfWeekBasedSetting) return dayOfWeekBasedSetting.timeSlots

  return []
}

export const getBookingsEtMap = (bookings: IRecord_Booking_Core_Viewable[]): Record<string, Record<string, IRecord_Booking_Core_Viewable[]>> => {
  const bookingsMap: Record<string, Record<string, IRecord_Booking_Core_Viewable[]>> = {}
  bookings.forEach(booking => {
    const useDate = getDateString(new Date(booking.data.date))
    const dateMap = bookingsMap[useDate] || {}
    const etArray = dateMap[booking.assigned_et] || []
    etArray.push(booking)
    dateMap[booking.assigned_et] = etArray
    bookingsMap[useDate] = dateMap
  })
  return bookingsMap
}

// for each day in the range - return an array of et time slots
export const getPublicCalendar = ({
  settings,
  bookings,
  dates,
  length,
  postcode = '',
}: {
  settings: IData_Booking_Settings_Org,
  bookings: IRecord_Booking_Core_Viewable[],
  dates: string[],
  length: number,
  postcode?: string,
}): IData_Calendar_Public_Day[] => {

  const outcode = postcode ? getOutcode(postcode) : ''

  let {
    travel_time_before,
    travel_time_after,
    slot_interval,
  } = settings.settings.general_config

  length = parseInt(length as unknown as string)
  if(isNaN(length)) throw new Error(`length is not a number ${length}`)

  travel_time_before = parseInt(travel_time_before as unknown as string)
  if(isNaN(travel_time_before)) throw new Error(`travel_time_before is not a number ${travel_time_before}`)

  travel_time_after = parseInt(travel_time_after as unknown as string)
  if(isNaN(travel_time_after)) throw new Error(`travel_time_after is not a number ${travel_time_after}`)

  slot_interval = parseInt(slot_interval as unknown as string)
  if(isNaN(slot_interval)) throw new Error(`slot_interval is not a number ${slot_interval}`)

  const bookingsMap = getBookingsEtMap(bookings)

  return dates.map(date => {
    const bookingsForDay = bookingsMap[date] || {}

    //let slotsForDay: IData_Booking_Availability_Time_Slot[] = []
    const ets = settings.ets
      .filter(et => {
        return doesETCoverOutcode(et, outcode)
      })
      .map(et => {
        // these are slots for a single ET without looking at the booking records
        const slots = getAvailableTimeSlots(
          et.availability,
          date,
        )

        const bookingsForEt = bookingsForDay[et.id] || []

        // these are the time slots that can have a booking added
        // at the given length for the given ET without it clashing
        let canBookSlots: IData_Booking_Availability_Time[] = []

        slots.forEach(availabilitySlot => {

          // factor the travel to time into the start
          // 8am
          // booking is 2 hours
          // travel_time_before = 30 mins
          // total booking time = 2.5 hours
          // so - first actual slot = 8.30

          // so we start at 8.30 because it will take 30 mins to travel
          // and bob starts at 8am
          let currentStart = adjustTime(availabilitySlot.start, travel_time_before)
          let currentStartDate = new Date()
          currentStartDate.setHours(currentStart.hour)
          currentStartDate.setMinutes(currentStart.min)

          const slotDate = currentStartDate
          let adjustedDate = currentStartDate

          while(isTimeBeforeOrSame(adjustTime(currentStart, parseInt(`${length}`) + parseInt(`${travel_time_after}`)), availabilitySlot.end) && (getDateString(slotDate) == getDateString(adjustedDate))) {
            canBookSlots.push(currentStart)
            currentStart = adjustTime(currentStart, slot_interval)
            adjustedDate = new Date(adjustedDate.getTime() + slot_interval * 60000)
          }
        })

        canBookSlots = canBookSlots.filter(slot => {
          const clashingBooking = bookingsForEt.find(booking => {

            // booking is at 10am
            // booking is 2 hours
            // travel time to = 30 mins
            // travel time affter = 0 mins

            // this is 10am
            const existingBookingPublicStart = getTimeFromDateString(booking.data.date)

            // this is 9.30
            const existingBookingActualStart = adjustTime(existingBookingPublicStart, -travel_time_before)

            // this is 10am + 2 hours + 0 mins
            const existingBookingActualEnd = adjustTime(existingBookingPublicStart, booking.data.product.length + travel_time_after)

            const slotActualStart = adjustTime(slot, -travel_time_before)
            const slotActualEnd = adjustTime(slot, length + travel_time_after)
            
            return doTimeSlotsIntersect({
              start: existingBookingActualStart,
              end: existingBookingActualEnd,
            }, {
              start: slotActualStart,
              end: slotActualEnd,
            })
          })

          return clashingBooking ? false : true
        })

        return {
          id: et.id,
          slots: canBookSlots,
        }
      })

    return {
      date,
      ets,
    }
  })
}

export const mergePublicCalendar = (days: IData_Calendar_Public_Day[]): IData_Calendar_Public_Day_Merged[] => {

  return days.map<IData_Calendar_Public_Day_Merged>(day => {

    const uniqueSlotMap: Record<string, IData_Calendar_Public_Slot_Merged> = {}

    day.ets.forEach(et => {
      et.slots.forEach(slot => {
        const slotId = [slot.hour,slot.min].join(':')
        if(uniqueSlotMap[slotId]) {
          uniqueSlotMap[slotId].ets.push(et.id)
          return
        }
        else {
          uniqueSlotMap[slotId] = Object.assign({}, slot, {
            ets: [et.id],
          })
        }
        
      })
    })

    const uniqueSlots = Object.values(uniqueSlotMap)

    uniqueSlots.sort((a, b) => {
      if(a.hour < b.hour) return -1
      if(a.hour > b.hour) return 1
      if(a.min < b.min) return -1
      if(a.min > b.min) return 1
      return 0
    })

    return {
      date: day.date,
      slots: uniqueSlots,
    }
  })
}